import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Card, Badge, Button, Form, Spinner, Row, Col
} from "react-bootstrap";
import {
  LuArrowLeft, LuPackage, LuTag, LuBarcode,
  LuPencil, LuSave, LuX, LuTrash2, LuPlus,
  LuHistory, LuDollarSign, LuLayers
} from "react-icons/lu";
import {
  getProductDetail,
  updateProduct,
  deleteProduct,
  uploadProductImages,
  removeProductImage,
  getCategoryList
} from "../../../redux/reducers/productSlice";
import { toast } from "react-toastify";
import ReactSelect from "react-select";
import ConfirmModal from "../../../components/ConfirmModal";

export default function ViewProduct() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const businessId = selectedBusiness?._id;
  const { productDetail, categoryList, loading } = useSelector((state) => state.product);

  const [editing, setEditing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showRemoveImgModal, setShowRemoveImgModal] = useState(false);
  const [imgToRemove, setImgToRemove] = useState("");
  const [form, setForm] = useState({
    name: "", description: "", productCategory: "",
    productType: "item", quantity: 0, price: 0,
    pricingTiers: []
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  // Load data
  useEffect(() => {
    if (id) {
      dispatch(getProductDetail(id));
      if (businessId) {
        dispatch(getCategoryList(businessId));
      }
    }
  }, [dispatch, id, businessId]);

  // Populate form
  useEffect(() => {
    if (productDetail) {
      setForm({
        id: productDetail._id,
        name: productDetail.name || "",
        description: productDetail.description || "",
        productCategory: productDetail.productCategory?._id || "",
        productType: productDetail.productType || "item",
        quantity: productDetail.quantity || 0,
        price: productDetail.price || 0,
        pricingTiers: productDetail.pricingTiers || [],
        business: businessId
      });
    }
  }, [productDetail, businessId]);

  const handleFieldChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const handleSave = () => {
    dispatch(updateProduct(form)).then((res) => {
      if (!res.error) {
        if (selectedFiles.length > 0) {
           dispatch(uploadProductImages({ id, files: selectedFiles })).then(() => {
             setSelectedFiles([]);
             setPreviews([]);
             setEditing(false);
             dispatch(getProductDetail(id));
           });
        } else {
          setEditing(false);
          toast.success("Product updated successfully");
          dispatch(getProductDetail(id));
        }
      }
    });
  };

  const handleDelete = () => {
    dispatch(deleteProduct(id)).then((res) => {
      if (!res.error) navigate("/product");
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalCurrentImages = (productDetail?.images?.length || 0) + selectedFiles.length;
    if (totalCurrentImages + files.length > 5) {
      toast.warning("Max 5 images allowed");
      return;
    }
    setSelectedFiles([...selectedFiles, ...files]);
    setPreviews([...previews, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeFile = (idx) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(idx, 1);
    setSelectedFiles(newFiles);
    const newPreviews = [...previews];
    URL.revokeObjectURL(newPreviews[idx]);
    newPreviews.splice(idx, 1);
    setPreviews(newPreviews);
  };

  const handleRemoveExistingImage = (imageUrl) => {
    setImgToRemove(imageUrl);
    setShowRemoveImgModal(true);
  };

  if (loading && !productDetail) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!productDetail) {
    return (
      <div className="text-center py-5">
        <LuPackage size={48} className="text-muted opacity-25 mb-3" />
        <p className="text-muted">Product not found.</p>
        <Button variant="light" className="rounded-pill" onClick={() => navigate("/product")}>
          Back to Products
        </Button>
      </div>
    );
  }

  const p = productDetail;

  return (
    <div className="container-fluid my-4">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-light rounded-circle p-2" onClick={() => navigate("/product")}>
          <LuArrowLeft size={16} />
        </button>
        <div className="d-flex align-items-center gap-3">
          <div className="bg-light rounded-3 p-2 text-muted shadow-sm">
            <LuPackage size={24} />
          </div>
          <div>
            <h5 className="fw-bold text-dark mb-0">{p.name}</h5>
            <div className="text-muted small">
              Created {moment(p.createdAt).format("MMM D, YYYY")}
            </div>
          </div>
        </div>
        <div className="ms-auto d-flex gap-2">
          {!editing ? (
            <Button
              variant="outline-primary"
              className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
              size="sm"
              onClick={() => setEditing(true)}
            >
              <LuPencil size={14} /> Edit
            </Button>
          ) : (
            <>
              <Button
                variant="light"
                className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
                size="sm"
                onClick={() => setEditing(false)}
              >
                <LuX size={14} /> Cancel
              </Button>
              <Button
                variant="primary"
                className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
                size="sm"
                onClick={handleSave}
                style={{ background: "#28B0C4", border: "none" }}
              >
                <LuSave size={14} /> Save
              </Button>
            </>
          )}
          <Button
            variant="outline-danger"
            className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
            size="sm"
            onClick={() => setDeleteModal(true)}
          >
            <LuTrash2 size={14} /> Delete
          </Button>
        </div>
      </div>

      <Row className="g-4">
        {/* Main Info */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
            <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
              <LuPackage className="text-brand" size={18} />
              <h6 className="fw-bold mb-0">General Information</h6>
            </div>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Product Name</Form.Label>
                  {editing ? (
                    <Form.Control
                      className="bg-light border-0 rounded-3 fw-semibold"
                      value={form.name}
                      onChange={(e) => handleFieldChange("name", e.target.value)}
                    />
                  ) : (
                    <div className="fw-bold fs-5">{p.name}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Description</Form.Label>
                  {editing ? (
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="bg-light border-0 rounded-3"
                      value={form.description}
                      onChange={(e) => handleFieldChange("description", e.target.value)}
                    />
                  ) : (
                    <div className="text-muted small">{p.description || "No description provided."}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Category</Form.Label>
                  {editing ? (
                    <ReactSelect
                      options={categoryList.map(c => ({ label: c.name, value: c._id }))}
                      value={categoryList.filter(c => c._id === form.productCategory).map(c => ({ label: c.name, value: c._id }))[0]}
                      onChange={(opt) => handleFieldChange("productCategory", opt.value)}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  ) : (
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <Badge bg="light" className="text-dark border rounded-pill fw-normal px-3 py-2">
                        <LuTag size={12} className="me-1" />
                        {p.productCategory?.name || "Uncategorized"}
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Type</Form.Label>
                  <div className="mt-2">
                    {editing ? (
                      <div className="d-flex gap-3">
                        <Form.Check
                          type="radio"
                          label="Item"
                          name="productType"
                          checked={form.productType === "item"}
                          onChange={() => handleFieldChange("productType", "item")}
                        />
                        <Form.Check
                          type="radio"
                          label="Service"
                          name="productType"
                          checked={form.productType === "service"}
                          onChange={() => handleFieldChange("productType", "service")}
                        />
                      </div>
                    ) : (
                      <Badge bg={p.productType === 'service' ? 'info-subtle' : 'success-subtle'} className={`text-${p.productType === 'service' ? 'info' : 'success'} rounded-pill px-3`}>
                        {p.productType === 'service' ? 'Service' : 'Physical Item'}
                      </Badge>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Card>

          <Card className="border-0 shadow-sm rounded-4 p-4">
            <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
              <LuDollarSign className="text-brand" size={18} />
              <h6 className="fw-bold mb-0">Pricing & Inventory</h6>
            </div>

            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Base Price</Form.Label>
                  {editing ? (
                    <Form.Control
                      type="number"
                      className="bg-light border-0 rounded-3 fw-bold"
                      value={form.price}
                      onChange={(e) => handleFieldChange("price", e.target.value)}
                    />
                  ) : (
                    <div className="fw-bold fs-4 text-brand">${p.price?.toFixed(2)}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted text-uppercase">Stock Quantity</Form.Label>
                  {editing ? (
                    <Form.Control
                      type="number"
                      className="bg-light border-0 rounded-3 fw-bold"
                      value={form.quantity}
                      onChange={(e) => handleFieldChange("quantity", e.target.value)}
                      disabled={form.productType === 'service'}
                    />
                  ) : (
                    <div className="fw-bold fs-4">
                      {p.productType === 'service' ? <span className="text-muted">N/A</span> : p.quantity}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Tiered Pricing */}
              <Col md={12}>
                <Form.Label className="small fw-bold text-muted text-uppercase mb-3">Volume Discount Tiers</Form.Label>
                {editing ? (
                   <div className="bg-light p-3 rounded-4">
                      {form.pricingTiers.map((tier, idx) => (
                        <Row key={idx} className="g-2 mb-2 align-items-end">
                          <Col>
                            <label className="extra-small text-muted mb-1">Min Qty</label>
                            <Form.Control 
                              size="sm"
                              type="number"
                              value={tier.minQuantity}
                              onChange={(e) => {
                                const newTiers = [...form.pricingTiers];
                                newTiers[idx] = { ...tier, minQuantity: e.target.value };
                                handleFieldChange("pricingTiers", newTiers);
                              }}
                            />
                          </Col>
                          <Col>
                            <label className="extra-small text-muted mb-1">Price ($)</label>
                            <Form.Control 
                              size="sm"
                              type="number"
                              value={tier.price}
                              onChange={(e) => {
                                const newTiers = [...form.pricingTiers];
                                newTiers[idx] = { ...tier, price: e.target.value };
                                handleFieldChange("pricingTiers", newTiers);
                              }}
                            />
                          </Col>
                          <Col xs="auto">
                             <Button variant="outline-danger" size="sm" onClick={() => {
                               const newTiers = form.pricingTiers.filter((_, i) => i !== idx);
                               handleFieldChange("pricingTiers", newTiers);
                             }}><LuTrash2 size={14}/></Button>
                          </Col>
                        </Row>
                      ))}
                      <Button variant="outline-primary" size="sm" className="mt-2 rounded-pill px-3" onClick={() => handleFieldChange("pricingTiers", [...form.pricingTiers, { minQuantity: "", price: "" }])}>
                        <LuPlus size={14} className="me-1"/> Add Tier
                      </Button>
                   </div>
                ) : (
                  <Row className="g-2">
                    {p.pricingTiers?.length > 0 ? p.pricingTiers.map((tier, idx) => (
                      <Col key={idx} md={4}>
                        <div className="bg-light p-2 rounded-3 text-center border">
                          <div className="text-muted extra-small fw-bold">{tier.minQuantity}+ Units</div>
                          <div className="fw-bold text-dark">${tier.price?.toFixed(2)}</div>
                        </div>
                      </Col>
                    )) : (
                      <Col><div className="text-muted small italic">No volume discounts configured.</div></Col>
                    )}
                  </Row>
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Sidebar Info */}
        <Col lg={4}>
          {/* Images */}
          <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
             <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
              <LuLayers className="text-brand" size={18} />
              <h6 className="fw-bold mb-0">Product Images</h6>
            </div>
            
            <div className="d-flex flex-wrap gap-2">
               {p.images?.map((img, idx) => (
                 <div key={idx} className="position-relative" style={{ width: '80px', height: '80px' }}>
                    <img src={img} className="rounded border shadow-sm w-100 h-100" style={{ objectFit: 'cover' }} alt=""/>
                    {editing && (
                      <button className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 m-n1 p-0" style={{ width: 20, height: 20 }} onClick={() => handleRemoveExistingImage(img)}>
                        <LuX size={12}/>
                      </button>
                    )}
                 </div>
               ))}
               {editing && previews.map((prev, idx) => (
                 <div key={`prev-${idx}`} className="position-relative" style={{ width: '80px', height: '80px' }}>
                    <img src={prev} className="rounded border shadow-sm w-100 h-100" style={{ objectFit: 'cover' }} alt=""/>
                    <button className="btn btn-danger btn-sm rounded-circle position-absolute top-0 end-0 m-n1 p-0" style={{ width: 20, height: 20 }} onClick={() => removeFile(idx)}>
                      <LuX size={12}/>
                    </button>
                 </div>
               ))}
               {editing && (p.images?.length || 0) + selectedFiles.length < 5 && (
                 <label className="d-flex align-items-center justify-content-center border border-dashed rounded bg-light cursor-pointer text-muted" style={{ width: 80, height: 80, cursor: 'pointer' }}>
                    <LuPlus size={24}/>
                    <input type="file" multiple className="d-none" onChange={handleFileChange} />
                 </label>
               )}
            </div>
            {!editing && (!p.images || p.images.length === 0) && (
              <div className="text-center py-4 bg-light rounded-4">
                <LuPackage size={32} className="text-muted opacity-25" />
                <div className="text-muted extra-small mt-2">No images uploaded</div>
              </div>
            )}
          </Card>

          {/* Barcode */}
          <Card className="border-0 shadow-sm rounded-4 p-4">
             <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
              <LuBarcode className="text-brand" size={18} />
              <h6 className="fw-bold mb-0">Barcode & Tracking</h6>
            </div>
            
            {p.barcode?.url ? (
              <div className="text-center">
                <img src={p.barcode.url} className="img-fluid border rounded p-2 mb-2 bg-white" alt="Barcode" style={{ maxHeight: 60 }}/>
                <div className="font-monospace fw-bold">{p.barcode.sku}</div>
              </div>
            ) : (
              <div className="text-center py-3 text-muted small">
                Barcode generation pending...
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Confirm Modals */}
      <ConfirmModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action is permanent and cannot be undone."
        confirmText="Delete Permanently"
        variant="danger"
        icon={LuTrash2}
      />

      <ConfirmModal
        show={showRemoveImgModal}
        onHide={() => setShowRemoveImgModal(false)}
        onConfirm={() => {
            dispatch(removeProductImage({ id, imageUrl: imgToRemove }));
            setShowRemoveImgModal(false);
        }}
        title="Remove Image"
        message="Are you sure you want to remove this product image? This action will immediately update the product listing."
        confirmText="Yes, Remove"
        variant="danger"
        icon={LuTrash2}
      />
    </div>
  );
}
